import React, { createContext, useCallback, useContext, useState, useMemo, } from "react";
import WebMercatorViewport from "viewport-mercator-project";
import { FlyToInterpolator } from "react-map-gl";
import bbox from "@turf/bbox";
import { useContextWarning } from "../util";
const ViewportContext = createContext(undefined);
const UpdateViewportContext = createContext(undefined);
const MapboxRefContext = createContext(undefined);
/**
 * A [react context](https://reactjs.org/docs/context.html) provider that can store and manipulate a web mercator viewport. Use this to
 * control your map anywhere in your app. Wrap your app in <MapProvider />, then check out the
 * API Documentation for available hooks and usage. [Read why we've split getting and setting](https://kentcdodds.com/blog/how-to-use-react-context-effectively) the viewport into separate hooks.
 */
export const MapProvider = ({ children }) => {
    const [viewport, setViewport] = useState();
    const [globalBounds, setGlobalBounds] = useState();
    /**
     * This function allows you to change the viewport but always remain in specific bounds.
     *
     * @param viewportOrViewportUpdater the viewport that you would like to update the map to or an updater function.
     * @param localBounds optionally pass in specific bounds, otherwise it'll use globalBounds.
     */
    const setViewportInBounds = useCallback((viewportOrViewportUpdater, localBounds) => {
        setViewport(previousViewport => {
            /**
             * Can pass either a function or an object. You'd
             * pass a function to get access to the previous
             * viewState.
             */
            const nextViewport = typeof viewportOrViewportUpdater === "function"
                ? viewportOrViewportUpdater(previousViewport)
                : viewportOrViewportUpdater;
            /** Which bounds are we using this call? */
            const bounds = localBounds || globalBounds;
            /** Return early if no bounds have been set */
            if (!bounds)
                return nextViewport;
            /** Get the variables we need for calculation */
            let { latitude, longitude, ...rest } = nextViewport;
            const [[minLongitude, minLatitude], [maxLongitude, maxLatitude],] = bounds;
            /** Clamp latitude */
            if (latitude < minLatitude) {
                latitude = minLatitude;
            }
            else if (latitude > maxLatitude) {
                latitude = maxLatitude;
            }
            /** Clamp longitude */
            if (longitude < minLongitude) {
                longitude = minLongitude;
            }
            else if (longitude > maxLongitude) {
                longitude = maxLongitude;
            }
            /** Return clamped viewport */
            return {
                ...previousViewport,
                ...rest,
                latitude,
                longitude,
            };
        });
    }, [globalBounds]);
    // given a latlng, small adjustments might have to be made
    // around a given coordinate. We use this in the citydna projection
    // to make small adjustments for the physical model map.
    const [zoomAdjustment, setZoomAdjustment] = useState(0);
    const [bearingAdjustment, setBearingAdjustment] = useState(0);
    const adjustedViewport = viewport
        ? {
            ...viewport,
            zoom: viewport.zoom + zoomAdjustment,
            bearing: viewport.bearing + bearingAdjustment,
        }
        : {};
    // ref to store the mapbox instance
    const [map, setMap] = useState();
    /**
     * A helper function that animates the viewport to fit a given feature on-screen.
     * @param feature Feature<any> feature to fit the map to
     */
    const fitViewportToFeature = useCallback((feature, options = {}) => {
        if (!feature)
            throw Error("You must pass a feature to fitMapToFeature");
        if (!map)
            throw Error("Map hasn't loaded yet, be patient.");
        const bounds = bbox(feature);
        const { clientWidth: width, clientHeight: height } = map.getContainer();
        const vp = new WebMercatorViewport({ width, height });
        /** Clamp horizontal padding if required */
        if (typeof options.padding === "object" &&
            width < (options.padding.left || 0) + (options.padding.right || 0)) {
            options.padding.left = 0;
            options.padding.right = 0;
            console.warn("map width is less than padding width, resetting to 0px");
        }
        /** Clamp vertical padding if too wide */
        if (typeof options.padding === "object" &&
            width < (options.padding.top || 0) + (options.padding.bottom || 0)) {
            options.padding.top = 0;
            options.padding.bottom = 0;
            console.warn("map height is less than padding height, resetting to 0px");
        }
        const { latitude, longitude, zoom } = vp.fitBounds([
            [bounds[0], bounds[1]],
            [bounds[2], bounds[3]],
        ], options);
        setViewport({
            latitude,
            longitude,
            zoom,
            transitionDuration: 1500,
            transitionInterpolator: new FlyToInterpolator(),
            transitionEasing: (t) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
        });
    }, [map]);
    /** Memoise context value to prevent re-renders */
    const updateViewportContext = useMemo(() => ({
        fitViewportToFeature,
        setBearingAdjustment,
        setGlobalBounds,
        setViewport: setViewportInBounds,
        setViewportInBounds,
        setViewportWithoutBounds: setViewport,
        setZoomAdjustment,
    }), [fitViewportToFeature, setBearingAdjustment]);
    /** Memoise context value to prevent re-renders */
    const mapRefContext = useMemo(() => [map, setMap], [map, setMap]);
    return (React.createElement(ViewportContext.Provider, { value: {
            viewport,
            adjustedViewport,
            globalBounds,
            zoomAdjustment,
            bearingAdjustment,
        } },
        React.createElement(UpdateViewportContext.Provider, { value: updateViewportContext },
            React.createElement(MapboxRefContext.Provider, { value: mapRefContext }, children))));
};
/**
 * Map viewport setters. Useful for updating the map's viewport from anywhere in your app. Does not expose the viewport itself,
 * just the dispatchers (`setViewport`, `setGlobalBounds`, `setViewportInBounds`, `setZoomAdjustment`, `setBearingAdjustment` and `fitMapToFeature`).
 * See TypeDoc for more information.
 * @returns map viewport and bounds setters
 * @example
 * const {setViewport} = useUpdateMapViewport();
 */
export function useUpdateViewport() {
    const context = useContext(UpdateViewportContext);
    useContextWarning(context, "MapProvider");
    return context;
}
/**
 * Exposes map viewport getters `viewport`, `adjustedViewport`, `globalBounds`
 * WARNING: using this will cause your component to re-render continuously whenever the map is interacted with. Use sparingly.
 * @returns map viewport and bounds object
 * @example
 * const {viewport} = useMapViewport();
 */
export function useViewport() {
    const context = useContext(ViewportContext);
    useContextWarning(context, "MapProvider");
    return context;
}
/**
 * Stores the mapbox instance in a state variable.
 * @return [mapInstance|undefined, Function]
 */
export function useMapboxRef() {
    const context = useContext(MapboxRefContext);
    useContextWarning(context, "MapProvider");
    return context;
}
